
import BCXBreadcrumbs from '@/components/molecules/BCXBreadcrumbs.vue';
import SectionIcon from '@/components/svg/settings-big.svg?inline';
import useSettings from '@/state/settingsState';
import DeleteAccountPopup from '@/views/settings/components/popups/DeleteAccountPopup.vue';
import SettingVerificationPopup from '@/views/settings/components/popups/SettingsVerificationPopup.vue';
import SectionGeneral from '@/views/settings/components/sections/SectionGeneral.vue';
import SectionLegal from '@/views/settings/components/sections/SectionLegal.vue';
import SectionNotifications from '@/views/settings/components/sections/SectionNotifications.vue';
import SectionProfileSettings from '@/views/settings/components/sections/SectionProfileSettings.vue';
import SectionSecurity from '@/views/settings/components/sections/SectionSecurity.vue';
import SectionUserDataEmail from '@/views/settings/components/sections/SectionUserDataEmail.vue';
import SettingsSection from '@/views/settings/components/utils/SettingsSection.vue';
import {
  createSettingsSectionOpenState,
  resolveSettingsSection,
  SettingsSectionKey
} from '@/views/settings/utils/settingsSections';
import useUser from '@/mixins/useUser';
import scrollIntoView from '@/utils/scrollIntoView';
import {
  defineComponent, nextTick, ref, watch
} from 'vue';
import { useRoute, useRouter } from 'vue2-helpers/vue-router';

export default defineComponent({
  name: 'Settings',
  components: {
    DeleteAccountPopup,
    SettingVerificationPopup,
    SectionLegal,
    SectionGeneral,
    SectionNotifications,
    SectionProfileSettings,
    SectionSecurity,
    SectionUserDataEmail,
    SettingsSection,
    BCXBreadcrumbs,
    SectionIcon
  },
  setup() {
    const { isFreelancer } = useUser('self');
    const { loadSettings, pendingVerification, showDeleteAccountPopup } = useSettings();
    const route = useRoute();
    const router = useRouter();

    const showVerificationPopup = ref(false);
    const sectionOpenState = ref(createSettingsSectionOpenState(null, isFreelancer.value));

    watch(pendingVerification, (verification) => {
      if (verification) {
        showVerificationPopup.value = true;
      }
    }, { immediate: true });

    loadSettings();

    const setSectionOpen = (sectionKey:SettingsSectionKey, isOpen:boolean) => {
      sectionOpenState.value = {
        ...sectionOpenState.value,
        [sectionKey]: isOpen
      };
    };

    const syncSectionFromRoute = async () => {
      const routeSection = typeof route.params?.section === 'string'
        ? route.params.section
        : undefined;
      const activeSection = resolveSettingsSection(routeSection, isFreelancer.value);

      if (routeSection && !activeSection) {
        await router.replace({ name: 'settings' });
        return;
      }

      sectionOpenState.value = createSettingsSectionOpenState(activeSection, isFreelancer.value);

      if (!activeSection) return;

      await nextTick();
      const targetElement = document.getElementById(activeSection);
      if (targetElement) {
        scrollIntoView(targetElement);
      }
    };

    watch(
      [() => route.params?.section, isFreelancer],
      () => {
        syncSectionFromRoute();
      },
      { immediate: true }
    );

    const onCloseVerificationPanel = () => {
      pendingVerification.value = null;
      showVerificationPopup.value = false;
    };

    return {
      isFreelancer,
      showVerificationPopup,
      onCloseVerificationPanel,
      sectionOpenState,
      setSectionOpen,
      showDeleteAccountPopup,
    };
  }
});
